import React, { useCallback, useEffect, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import { useParams } from "react-router-dom";
import {
  cancelManageReservationApi,
  getManageReservationApi,
  getManageRescheduleOptionsApi,
  rescheduleManageReservationApi,
} from "../../api/api";
import { ResultShell } from "../cumpleanos/BirthdaySteps";
import { formatLongDate } from "../beauty/beautyBooking";
import RescheduleModal from "./components/RescheduleModal";
import { deadlineLabel, reasonMessage, remainingLabel, unwrap } from "./components/rescheduleModel";
import { resumenCompacto } from "../../helpers/beautyLineas";
import "./reservas.css";
import "./components/reschedule.css";
import "../cumpleanos/cumpleanos.css";

const STATUS_LABELS = {
  pending: "Pendiente",
  reserved: "Reservado",
  confirmed: "Confirmado",
  cancelled: "Cancelado",
  expired: "Expirado",
  no_show: "No asistió",
  completed: "Realizado",
};

const nameOf = (value) => (value && typeof value === "object" ? value.name : value) || "";

// The summary is tolerant to either `date/slotStart/slotEnd` or a `time` string.
const timeOf = (view) => {
  const start = view.slotStart || view.time || "";
  return view.slotEnd && start ? `${start} – ${view.slotEnd}` : start;
};

const Row = ({ label, value }) =>
  value ? (
    <div className="bday-row">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  ) : null;

/** Guest page of the link emailed with a beauty turno (/reservas/gestionar/:token). Works without login. */
const ManageReservation = () => {
  const { token } = useParams();
  const [state, setState] = useState({ status: "loading", view: null, error: "" });
  const [rescheduling, setRescheduling] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [actionError, setActionError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  // A bad token and a missing reservation are indistinguishable on purpose.
  const invalid = () => setState({ status: "invalid", view: null, error: "" });

  useEffect(() => {
    let active = true;
    getManageReservationApi(token)
      .then((res) => {
        if (!active) return;
        const view = unwrap(res);
        setState(view ? { status: "ready", view, error: "" } : { status: "invalid", view: null, error: "" });
      })
      .catch((err) => {
        if (!active) return;
        if (err?.response?.status === 404) invalid();
        else {
          setState({
            status: "error",
            view: null,
            error: err?.response?.data?.message || "No pudimos cargar tu turno. Probá de nuevo.",
          });
        }
      });
    return () => {
      active = false;
    };
  }, [token, reloadKey]);

  const reload = useCallback(() => {
    setState({ status: "loading", view: null, error: "" });
    setReloadKey((k) => k + 1);
  }, []);

  const cancel = async () => {
    setCancelling(true);
    setActionError("");
    try {
      await cancelManageReservationApi(token);
      setConfirmingCancel(false);
      setCancelling(false);
      reload();
    } catch (err) {
      setCancelling(false);
      if (err?.response?.status === 404) {
        invalid();
        return;
      }
      setConfirmingCancel(false);
      setActionError(err?.response?.data?.message || "No pudimos cancelar el turno. Probá de nuevo.");
    }
  };

  if (state.status === "loading") {
    return (
      <div className="reservas-root bday">
        <div className="bday-state">
          <Spinner animation="border" />
        </div>
      </div>
    );
  }

  if (state.status === "invalid") {
    return (
      <div className="reservas-root bday">
        <ResultShell icon="search" tone="neutral" title="Link inválido o vencido">
          <p>Revisá que lo hayas copiado completo desde el email.</p>
        </ResultShell>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="reservas-root bday">
        <ResultShell icon="alert" tone="danger" title="No pudimos cargar tu turno">
          <p>{state.error}</p>
          <div className="bday-state-actions">
            <button type="button" className="r-btn-primary" onClick={reload}>
              Reintentar
            </button>
          </div>
        </ResultShell>
      </div>
    );
  }

  const view = state.view;
  const services = resumenCompacto(Array.isArray(view.services) ? view.services : []);
  const hints = [remainingLabel(view.remaining), deadlineLabel(view.deadline)].filter(Boolean);
  const date = view.date ? formatLongDate(view.date) : "";
  // The back explains (`rescheduleReason`) why a live turno cannot be moved: limit, deadline, paid at the salon.
  const showBlockedReason = !view.canReschedule && Boolean(view.rescheduleReason) && ["reserved", "confirmed"].includes(view.status);

  return (
    <div className="reservas-root bday">
      <ResultShell icon="cal" tone="neutral" title="Tu turno">
        <div className="bday-summary">
          <Row label="Salón" value={nameOf(view.salon)} />
          <Row label="Servicios" value={services} />
          <Row label="Profesional" value={nameOf(view.professional)} />
          <Row label="Fecha" value={date} />
          <Row label="Horario" value={timeOf(view) && `${timeOf(view)} hs`} />
          <Row label="Estado" value={STATUS_LABELS[view.status] || view.status} />
        </div>

        {hints.length > 0 && (view.canReschedule || view.canCancel) && <p>{hints.join(" · ")}</p>}
        {showBlockedReason && <p>{reasonMessage(view.rescheduleReason)}</p>}
        {actionError && <Alert variant="danger">{actionError}</Alert>}

        {confirmingCancel ? (
          <div className="rs-confirm" role="alertdialog" aria-label="Confirmar cancelación">
            <p>¿Seguro que querés cancelar este turno? Esta acción no se puede deshacer.</p>
            <div className="bday-state-actions">
              <button type="button" className="r-btn-soft" disabled={cancelling} onClick={() => setConfirmingCancel(false)}>
                No, conservar
              </button>
              <button type="button" className="r-btn-primary" disabled={cancelling} onClick={cancel}>
                {cancelling ? <Spinner animation="border" size="sm" /> : "Sí, cancelar turno"}
              </button>
            </div>
          </div>
        ) : (
          (view.canReschedule || view.canCancel) && (
            <div className="bday-state-actions">
              {view.canReschedule && (
                <button type="button" className="r-btn-primary" onClick={() => setRescheduling(true)}>
                  Reagendar
                </button>
              )}
              {view.canCancel && (
                <button type="button" className="r-btn-soft" onClick={() => setConfirmingCancel(true)}>
                  Cancelar turno
                </button>
              )}
            </div>
          )
        )}
      </ResultShell>

      {rescheduling && (
        <RescheduleModal
          title="Reagendar turno"
          initialDate={view.date}
          summary={[nameOf(view.salon), services, date, timeOf(view) && `${timeOf(view)} hs`].filter(Boolean).join(" · ")}
          loadOptions={(d) => getManageRescheduleOptionsApi(token, d)}
          onConfirm={({ date: d, slotStart }) => rescheduleManageReservationApi(token, { date: d, slotStart })}
          onClose={() => setRescheduling(false)}
          onDone={() => {
            setRescheduling(false);
            reload();
          }}
        />
      )}
    </div>
  );
};

export default ManageReservation;
